import { useCallback, useEffect, useState } from 'react'
import Sheet from '../components/Sheet.jsx'
import {
  addReminder, addTask, fileEntry, getInbox,
} from '../lib/notes.js'
import { supabase } from '../lib/supabase.js'
import { shortDate, todayISO } from '../lib/format.js'

/**
 * Everything captured and not yet filed.
 *
 * Capture deliberately asks nothing, which is what makes it fast. This is
 * where that debt gets paid, on his terms and in a batch, rather than as a
 * toll at the moment of having the thought.
 *
 * A capture leaves here as one of four things: filed to a category, a task
 * with a date, a reminder, or deleted.
 */
export default function Inbox({ categories, toast, onChanged }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      setRows(await getInbox())
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function done(msg) {
    setSheet(null)
    await load()
    onChanged?.()
    if (msg) toast(msg)
  }

  if (error) {
    return (
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>Could not read your inbox. {error}</p>
      </div>
    )
  }
  if (!rows) return <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p>

  return (
    <>
      <div className="page-head">
        <span className="lbl">Inbox</span>
        <h1 className="serif">Not filed yet</h1>
      </div>

      {rows.length === 0 ? (
        <div className="empty">
          <h2 className="serif">All clear</h2>
          <p>Anything you capture lands here until you say where it belongs.</p>
        </div>
      ) : null}

      {rows.map((e) => (
        <div className="card inbox-item" key={e.id}>
          <div className="pad">
            <p className="inbox-body">{e.body}</p>
            <span className="lbl">{shortDate(e.created_at.slice(0, 10))}</span>
          </div>
          <div className="build-acts">
            <span className="build-acts-right">
              <button type="button" onClick={() => setSheet({ kind: 'file', entry: e })}>
                File
              </button>
              <button type="button" onClick={() => setSheet({ kind: 'task', entry: e })}>
                Task
              </button>
              <button type="button" onClick={() => setSheet({ kind: 'remind', entry: e })}>
                Remind
              </button>
            </span>
            <button type="button" onClick={() => setSheet({ kind: 'drop', entry: e })}>
              Delete
            </button>
          </div>
        </div>
      ))}

      {sheet?.kind === 'file' ? (
        <Sheet title="File it" onClose={() => setSheet(null)}>
          <p style={{ margin: '0 0 14px', fontSize: 14 }}>{sheet.entry.body}</p>
          <div className="catpick">
            {categories.map((c) => (
              <button key={c.id} type="button" onClick={async () => {
                try {
                  await fileEntry(sheet.entry.id, c.id)
                  done('Filed to ' + c.name)
                } catch (err) {
                  toast(err.message || 'Could not file that.')
                }
              }}>
                <i style={{ background: c.color || 'var(--ink-faint)' }} />
                {c.name}
              </button>
            ))}
          </div>
        </Sheet>
      ) : null}

      {sheet?.kind === 'task' ? (
        <ConvertSheet
          title="Make it a task"
          entry={sheet.entry}
          categories={categories}
          withDate
          dateLabel="Due"
          onClose={() => setSheet(null)}
          onSave={async ({ body, categoryId, date }) => {
            await addTask({ body, categoryId, dueOn: date || null })
            /* The capture is filed, not deleted — the Log keeps one record and
               the note stays findable in search. */
            await fileEntry(sheet.entry.id, categoryId)
            done('Added to Today')
          }}
          toast={toast}
        />
      ) : null}

      {sheet?.kind === 'remind' ? (
        <ConvertSheet
          title="Set a reminder"
          entry={sheet.entry}
          categories={categories}
          withTime
          dateLabel="When"
          onClose={() => setSheet(null)}
          onSave={async ({ body, categoryId, at }) => {
            await addReminder({
              body,
              remindAt: new Date(at).toISOString(),
              targetType: 'entry',
              targetId: sheet.entry.id,
            })
            await fileEntry(sheet.entry.id, categoryId)
            done('Reminder set')
          }}
          toast={toast}
        />
      ) : null}

      {sheet?.kind === 'drop' ? (
        <Sheet title="Delete this capture?" onClose={() => setSheet(null)}>
          <p style={{ margin: '0 0 18px', fontSize: 14, color: 'var(--ink-dim)' }}>
            {sheet.entry.body}
          </p>
          <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--ink-dim)' }}>
            This removes it for good. The Log keeps the record that you captured it.
          </p>
          <div className="sheet-actions">
            <button type="button" className="btn ghost" onClick={() => setSheet(null)}>Keep it</button>
            <button type="button" className="btn" onClick={async () => {
              try {
                const { error: err } = await supabase
                  .from('entries').delete().eq('id', sheet.entry.id)
                if (err) throw err
                done('Deleted')
              } catch (err) {
                toast(err.message || 'Could not delete that.')
              }
            }}>Delete</button>
          </div>
        </Sheet>
      ) : null}
    </>
  )
}

/**
 * The body is editable on the way out. A capture is often a half sentence
 * typed in a hurry, and this is the natural moment to make it a real task.
 */
function ConvertSheet({
  title, entry, categories, withDate, withTime, dateLabel, onClose, onSave, toast,
}) {
  const [body, setBody] = useState(entry.body)
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? '')
  const [date, setDate] = useState(todayISO())
  const [at, setAt] = useState(() => {
    const d = new Date(Date.now() + 3600000)
    d.setMinutes(0, 0, 0)
    const p = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
  })
  const [busy, setBusy] = useState(false)

  return (
    <Sheet title={title} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !body.trim()) return
          setBusy(true)
          try {
            await onSave({ body: body.trim(), categoryId, date, at })
          } catch (err) {
            toast(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="cv-body">Wording</label>
          <textarea id="cv-body" rows={3} value={body}
            onChange={(e) => setBody(e.target.value)} />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="cv-cat">Category</label>
          <select id="cv-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {withDate ? (
          <div className="field">
            <label className="lbl" htmlFor="cv-date">{dateLabel}</label>
            <input id="cv-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
        ) : null}
        {withTime ? (
          <div className="field">
            <label className="lbl" htmlFor="cv-at">{dateLabel}</label>
            <input id="cv-at" type="datetime-local" required
              value={at} onChange={(e) => setAt(e.target.value)} />
          </div>
        ) : null}
        <button className="btn" type="submit" disabled={busy || !body.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </form>
    </Sheet>
  )
}
